import React, { useState, useRef, useEffect } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify, createSlug } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import QM from './TopbarDesktop/img/questionMark.png';
import { FaWhatsapp, FaRobot } from 'react-icons/fa';
import { SiCalendly } from 'react-icons/si';
import { AIChatWidget } from '../../../components/AIChat/AIChatWidget';

import css from './Topbar.module.css';
import { isStripeConnected } from '../../StripePayoutPage/StripePayoutPage.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    isLandingPage,
    fromWishlist,
    getIsStripeConnected,
    hasInsurancePending,
    insurancePendingListings = [],
    hasVehicleRegPending,
    vehicleRegPendingListings = [],
    currentUserListings = [],
    isListingPage = false,
    currentListing = null,
    listingReviews = [],
  } = props;

  const chatUnread = useSelector(state => state.chatUnread) || {};
  // Show dot if any unread for current user as customer or provider
  const hasAnyUnread = Object.values(chatUnread).some(obj => obj.customer || obj.provider);
  const chatNotificationCount = hasAnyUnread ? 1 : 0;

  const handleSubmit = values => {
    const { currentSearchParams, history, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  };

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      isListingPage={isListingPage}
      currentListing={currentListing}
      listingReviews={listingReviews}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE && resolvedCurrentPage === 'SearchPage';
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const [supportOpen, setSupportOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Close the chat on route change so context (listing vs. general) doesn't
  // linger from a previous page. The AIChatWidget below is keyed on mode+listing
  // to force a fresh iframe when it re-opens.
  useEffect(() => {
    setChatOpen(false);
  }, [location.pathname]);
  const [isVerified, setVerified] = useState(true);
  const [stripeRequirements, setStripeRequirements] = useState([]);
  const supportRef = useRef(null);

  const mobileUserType = currentUser?.attributes?.profile?.publicData?.userType;
  const showOwnerSupportOptions = !!currentUser && mobileUserType === 'owner' && currentUserHasListings === false;

  const WA_URL =
    'https://wa.me/447501526140?text=Hi%20%E2%80%94%20I%27m%20listing%20my%20motorhome%20on%20GoGallivanting%20and%20have%20a%20question.';
  const CALENDLY_URL = 'https://calendly.com/gogallivanting/30min';

  const loadCalendlyScript = () => {
    return new Promise(resolve => {
      if (window.Calendly) { resolve(); return; }
      if (document.querySelector('script[data-calendly]')) {
        document.querySelector('script[data-calendly]').addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.setAttribute('data-calendly', 'true');
      script.onload = () => resolve();
      document.body.appendChild(script);
    });
  };

  const openCalendly = () => {
    loadCalendlyScript().then(() => {
      if (window.Calendly) window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    });
    setSupportOpen(false);
  };

  const handleWhatsApp = () => {
    window.open(WA_URL, '_blank', 'noopener noreferrer');
    setSupportOpen(false);
  };

  const handleChatClick = () => {
    setChatOpen(true);
    setSupportOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (supportOpen && supportRef.current && !supportRef.current.contains(e.target)) {
        setSupportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [supportOpen]);

  const isEmailVerified = currentUser?.attributes?.emailVerified;
  const isStripeAccountConnected = currentUser?.attributes?.stripeConnected ?? false;
  const isUserVerified = currentUser?.attributes?.profile?.publicData?.isUserVerified ?? false;
  useEffect(() => {
    let isMounted = true;

    const run = async () => {
     if(isStripeAccountConnected){ 
      const requirements = await getIsStripeConnected();

      if (!isMounted) return;

      setStripeRequirements(requirements);
       const isValid =
         currentUser &&
         (!hasInsurancePending) &&
         isStripeAccountConnected &&
         currentUser.stripeAccount &&
         currentUser.attributes?.profile?.publicData?.connectedAccount === true &&
         requirements?.errors?.length === 0;
       requirements.disabled_reason === null;
       setVerified(isValid);
       if(props?.updateAiSearchStyle){
       props.updateAiSearchStyle(!isValid);}
     }
     else {
       setVerified(false);
       if (props?.updateAiSearchStyle) {
         props.updateAiSearchStyle(true);
       }
     }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, []);

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon className={css.searchMenuIcon} />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );

  const isPaymentSetupDone =
    isStripeAccountConnected &&
    currentUser?.stripeAccount &&
    currentUser?.attributes?.profile?.publicData?.connectedAccount === true &&
    stripeRequirements?.errors?.length === 0;

  const pendingItems = [
    !isEmailVerified && 'Email verification',
    hasInsurancePending && 'Insurance',
    !isPaymentSetupDone && 'Payment setup',
    hasVehicleRegPending && 'Vehicle registration',
  ].filter(Boolean);
  const pendingText = pendingItems.join(' · ');

  const finishSetupLink = (() => {
    if (!isEmailVerified || !isPaymentSetupDone) {
      return (
        <NamedLink name="AccountSettingsPage" className={css.verifyAccountButton}>
          <FormattedMessage id="Topbar.finishSetupButton" />
        </NamedLink>
      );
    }
    if (insurancePendingListings.length === 1) {
      const listing = insurancePendingListings[0];
      const slug = createSlug(listing.attributes.title);
      const id = listing.id.uuid;
      return (
        <NamedLink
          name="EditListingPage"
          params={{ slug, id, type: 'edit', tab: 'details' }}
          className={css.verifyAccountButton}
        >
          <FormattedMessage id="Topbar.finishSetupButton" />
        </NamedLink>
      );
    }
    if (insurancePendingListings.length > 1) {
      return (
        <NamedLink
          name="ManageListingsPage"
          params={{ tab: 'other' }}
          className={css.verifyAccountButton}
        >
          <FormattedMessage id="Topbar.finishSetupButton" />
        </NamedLink>
      );
    }
    if (vehicleRegPendingListings.length === 1) {
      const listing = vehicleRegPendingListings[0];
      const slug = createSlug(listing.attributes.title);
      const id = listing.id.uuid;
      return (
        <NamedLink
          name="EditListingPage"
          params={{ slug, id, type: 'edit', tab: 'verification' }}
          className={css.verifyAccountButton}
        >
          <FormattedMessage id="Topbar.finishSetupButton" />
        </NamedLink>
      );
    }
    return (
      <NamedLink
        name="ManageListingsPage"
        params={{ tab: 'other' }}
        className={css.verifyAccountButton}
      >
        <FormattedMessage id="Topbar.finishSetupButton" />
      </NamedLink>
    );
  })();

  const hasActiveListings = currentUserListings.some(
    l => !l.attributes.deleted && l.attributes.state === 'published'
  );

  return (
    <div className={classes}>
      {mobileUserType == 'owner' && hasActiveListings && (isVerified == false || hasVehicleRegPending) ?
        <div className={css.verifyWarningMobile}>
          {isMobileLayout ? <span><b>Action required to receive bookings</b><br></br>
            Your listing is visible. Complete the final step(s) below to unlock all owner features and start accepting bookings.<br></br>
            Complete: {pendingText}</span>
            : <span><b>Action required to receive bookings</b>|
              Your listing is visible. Complete the final step(s) below to unlock all owner features and start accepting bookings.
              Complete: {pendingText}</span>
          }
          <span style={{ padding: '05px 14px 14px 14px', zIndex: '9' }}>
            {finishSetupLink}
          </span>
        </div> :
        null}
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon className={css.menuIcon} />
          {notificationDot}
        </Button>
        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <div className={css.mobileSupportContainer} ref={supportRef}>
          <button
            className={css.mobileSupportTrigger}
            onClick={() => setSupportOpen(prev => !prev)}
            title="Get Support"
          >
            <img src={QM} width={36} alt="Support" />
          </button>
          {supportOpen && (
            <div className={css.mobileSupportDropdown}>
              <button className={css.mobileSupportItem} onClick={handleChatClick} title="Ask GoPilot™">
                <FaRobot />
              </button>
              {showOwnerSupportOptions && (
                <button className={css.mobileSupportItem} onClick={openCalendly} title="Book Your GoLive Call">
                  <SiCalendly />
                </button>
              )}
              {showOwnerSupportOptions && (
                <button className={css.mobileSupportItem} onClick={handleWhatsApp} title="WhatsApp">
                  <FaWhatsapp />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={isLandingPage ? desktopClassName : css.scrolled}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          isLandingPage={isLandingPage}
          chatNotificationCount={chatNotificationCount}
          isListingPage={isListingPage}
          currentListing={currentListing}
          listingReviews={listingReviews}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        fromWishlist={fromWishlist}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
      {chatOpen && (
        <AIChatWidget
          key={isListingPage ? `ctx-${currentListing?.id?.uuid}` : 'general'}
          listingData={isListingPage ? currentListing : null}
          reviews={isListingPage ? listingReviews : []}
          tenantId={process.env.REACT_APP_AYAKAN_AI_CLIENT_ID}
          userId={currentUser?.id?.uuid || null}
          theme="light"
          primaryColor="303b4b"
          mode={isListingPage ? 'context-chat' : 'general-chat'}
          title={isListingPage ? 'Ask GoPilot™' : 'GoPilot™ — Here to help'}
          hideTrigger
          isOpen={chatOpen}
          onOpenChange={setChatOpen}
        />
      )}
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const TopbarPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};
const mapDispatchToProps = dispatch => {
  return {
     getIsStripeConnected: () => dispatch(isStripeConnected())
  }
}

const Topbar = compose(
  withRouter,
  connect(null,
    mapDispatchToProps
  )
)(TopbarPage);
export default Topbar;
